import { useState, useRef } from "react";
import axios from "axios";
import { Star, Send, Loader2, ImagePlus, X, Video } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILES = 6;

export const ReviewForm = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: "", company: "", text: "" });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [media, setMedia] = useState([]); // {url, kind, content_type}
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: "", company: "", text: "" });
    setRating(0); setHover(0); setMedia([]);
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (media.length + files.length > MAX_FILES) {
      toast.error(`${MAX_FILES} fichiers maximum.`);
      return;
    }
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [...m, data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Échec de l'envoi de ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removeMedia = (i) => setMedia((m) => m.filter((_, k) => k !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim() || rating < 1) {
      toast.error("Ajoutez votre nom, une note et un commentaire.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, {
        name: form.name.trim(),
        company: form.company.trim(),
        rating,
        text: form.text.trim(),
        media,
      });
      toast.success("Merci ! Votre avis sera publié après validation.");
      reset();
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-brand-navy">Laisser un avis</DialogTitle>
          <DialogDescription>Partagez votre expérience et les photos/vidéos de votre colis reçu.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="review-form">
          <div className="space-y-2">
            <Label>Votre note *</Label>
            <div className="flex items-center gap-1" data-testid="review-star-selector">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`review-star-${n}`}
                  onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  className="p-0.5 transition-transform duration-200 hover:scale-110" aria-label={`${n} étoiles`}>
                  <Star size={28} className={(hover || rating) >= n ? "text-brand-gold" : "text-slate-300"} fill={(hover || rating) >= n ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rv-name">Nom *</Label>
              <Input id="rv-name" data-testid="review-name" value={form.name} onChange={set("name")} placeholder="Votre nom" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rv-company">Entreprise</Label>
              <Input id="rv-company" data-testid="review-company" value={form.company} onChange={set("company")} placeholder="(facultatif)" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rv-text">Votre avis *</Label>
            <Textarea id="rv-text" data-testid="review-text" rows={3} value={form.text} onChange={set("text")} placeholder="Décrivez votre expérience…" />
          </div>

          <div className="space-y-2">
            <Label>Photos / vidéos du colis</Label>
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={onPickFiles} data-testid="review-file-input" />
            <div className="flex flex-wrap gap-2">
              {media.map((m, i) => (
                <div key={i} className="relative h-16 w-16 rounded-md overflow-hidden border border-black/10 bg-slate-100" data-testid={`review-media-preview-${i}`}>
                  {m.kind === "image" ? (
                    <img src={`${BACKEND_URL}${m.url}`} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-brand-navy"><Video size={20} /></div>
                  )}
                  <button type="button" onClick={() => removeMedia(i)} data-testid={`remove-media-${i}`} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5">
                    <X size={12} />
                  </button>
                </div>
              ))}
              {media.length < MAX_FILES && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="add-media-btn"
                  className="h-16 w-16 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-brand-gold hover:text-brand-gold transition-colors disabled:opacity-50">
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={20} />}
                </button>
              )}
            </div>
            <p className="text-xs text-slate-400">Images (max 12 Mo) ou vidéos (max 80 Mo) — jusqu'à 6 fichiers.</p>
          </div>

          <DialogFooter>
            <button type="submit" disabled={loading || uploading} data-testid="review-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-sm font-bold uppercase tracking-wide text-white hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              {loading ? "Envoi…" : "Publier mon avis"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
